"use client";

import { memo, useCallback, useTransition } from "react";

import {
  type FilterUpdates,
  TutorialFilters as TutorialFiltersUI,
} from "@vllnt/ui";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

export type TutorialFiltersProps = {
  currentDifficulty: string;
  currentTags: string[];
  searchQuery: string;
  tags: string[];
};

function TutorialFiltersImpl({
  currentDifficulty,
  currentTags,
  searchQuery,
  tags,
}: TutorialFiltersProps): React.ReactNode {
  const { push } = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const t = useTranslations("tutorials");

  const labels = {
    activeFilters: t("active_filters"),
    clear: t("clear"),
    clearAll: t("clear_all"),
    difficulty: {
      advanced: t("difficulty.advanced"),
      all: t("difficulty.all"),
      beginner: t("difficulty.beginner"),
      intermediate: t("difficulty.intermediate"),
    },
    difficultyLabel: t("difficulty_label"),
    searchFilter: t("search_filter"),
    searchLabel: t("search_label"),
    searchPlaceholder: t("search_placeholder"),
    tagsLabel: t("tags_label"),
  };

  const handleFilterChange = useCallback(
    (updates: FilterUpdates) => {
      const parameters = new URLSearchParams(window.location.search);

      if (updates.difficulty !== undefined) {
        if (updates.difficulty === "all") {
          parameters.delete("difficulty");
        } else {
          parameters.set("difficulty", updates.difficulty);
        }
      }

      if (updates.search !== undefined) {
        if (updates.search === "") {
          parameters.delete("search");
        } else {
          parameters.set("search", updates.search);
        }
      }

      if (updates.tags !== undefined) {
        if (updates.tags.length === 0) {
          parameters.delete("tags");
        } else {
          parameters.set("tags", updates.tags.join(","));
        }
      }

      const queryString = parameters.toString();
      const url = queryString ? `${pathname}?${queryString}` : pathname;

      startTransition(() => {
        push(url);
      });
    },
    [pathname, push],
  );

  return (
    <TutorialFiltersUI
      currentDifficulty={currentDifficulty}
      currentTags={currentTags}
      isPending={isPending}
      labels={labels}
      onFilterChange={handleFilterChange}
      searchQuery={searchQuery}
      tags={tags}
    />
  );
}

export const TutorialFilters = memo(TutorialFiltersImpl);
TutorialFilters.displayName = "TutorialFilters";
